import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopsCustomMapBlockConfig } from '@front-platform/content-builder';
import { shopsCustomMapBlockResolveConfig } from '@front-platform/content-builder';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  input,
} from '@angular/core';
import { groupBy } from 'lodash';
import { Router } from '@angular/router';
import panzoom, { PanZoomOptions } from 'panzoom';
import { combineLatest, interval, merge, of, Subject, timer } from 'rxjs';
import { map, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';

import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { OrdersService } from '@core/services/orders/orders.service';
import { RouterHelperService } from 'src/app/core/services/router-helper/router-helper.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface ShopsCustomMapBlockRuntimeConfig {}

export type ShopsCustomMapBlockConfigInput = Partial<
  Omit<ShopsCustomMapBlockConfig, keyof ShopsCustomMapBlockRuntimeConfig> &
    ShopsCustomMapBlockRuntimeConfig
>;
export type ShopsCustomMapBlockResolvedConfig = Omit<
  ShopsCustomMapBlockConfig,
  keyof ShopsCustomMapBlockRuntimeConfig
> &
  Partial<ShopsCustomMapBlockRuntimeConfig>;
export interface ShopsCustomMapBlockContract extends ConfigComponentContract<
  ShopsCustomMapBlockConfigInput,
  ShopsCustomMapBlockResolvedConfig
> {}

@Component({
  selector: 'app-shops-custom-map-block',
  templateUrl: './shops-custom-map-block.component.html',
  styleUrls: ['./shops-custom-map-block.component.scss'],
  standalone: false,
})
export class ShopsCustomMapBlockComponent
  implements OnInit, OnDestroy, AfterViewInit, ShopsCustomMapBlockContract
{
  readonly config = input<ShopsCustomMapBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopsCustomMapBlockConfig,
    ShopsCustomMapBlockRuntimeConfig
  >(shopsCustomMapBlockResolveConfig, () => ({ config: this.config() }));
  @ViewChild('stage') stageElement!: ElementRef;
  get shops(): any {
    return this.resolvedConfig()['shops'] as unknown as any;
  }
  get image(): IContentBuilderFieldImage {
    return this.resolvedConfig()['image'] as unknown as IContentBuilderFieldImage;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get imageWidth(): number {
    return this.resolvedConfig()['image_width'] as unknown as number;
  }
  get imageHeight(): number {
    return this.resolvedConfig()['image_height'] as unknown as number;
  }
  get maxZoom(): number {
    return this.resolvedConfig()['max_zoom'] as unknown as number;
  }
  get initialPositionX(): number {
    return this.resolvedConfig()['initial_position_x'] as unknown as number;
  }
  get initialPositionY(): number {
    return this.resolvedConfig()['initial_position_y'] as unknown as number;
  }
  private _initialZoomConfigOverride: string | undefined;
  get initialZoom(): string {
    return (
      this._initialZoomConfigOverride ??
      (this.resolvedConfig()['initial_zoom'] as unknown as string)
    );
  }
  set initialZoom(value: string) {
    this._initialZoomConfigOverride = value;
  }

  public windowWidth = window.innerWidth;
  public windowHeight = window.innerHeight - 80;
  public zoom = 1;
  public shopsWithOrderReady: string[] = [];

  private marketplaceId = this.marketplaceSelector.selectMarketplaceId$;
  private initialPosition!: number[];
  private readonly destroyed$ = new Subject<boolean>();
  firstVisit!: boolean;

  public circlePosition?: number[];

  constructor(
    private readonly router: Router,
    private authSelector: AuthStoreSelector,
    private readonly ordersService: OrdersService,
    private readonly routerHelper: RouterHelperService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.initialPosition = [this.initialPositionX, this.initialPositionY];
    this.firstVisit = !JSON.parse(localStorage.getItem('map-visited') as string);
    if (this.firstVisit) {
      localStorage.setItem('map-visited', 'true');
    }

    this.loadOrders();
  }

  ngAfterViewInit(): void {
    let scale = 1;
    const minScale = Math.min(
      this.windowHeight / this.imageHeight,
      this.windowWidth / this.imageWidth,
    );

    if (this.initialZoom === 'contain') {
      scale = minScale;
    } else {
      scale = Math.max(this.windowHeight / this.imageHeight, this.windowWidth / this.imageWidth);
    }

    const options: PanZoomOptions = {
      maxZoom: scale * this.maxZoom,
      minZoom: minScale,
      initialX:
        this.windowWidth / 2 -
        (this.imageWidth * scale) / 2 +
        (this.initialPosition[0] - 0.5) * (this.windowWidth - this.imageWidth * scale),
      initialY:
        this.windowHeight / 2 -
        (this.imageHeight * scale) / 2 +
        (this.initialPosition[1] - 0.5) * (this.windowHeight - this.imageHeight * scale),
      initialZoom: 0.1,
    };

    panzoom(this.stageElement.nativeElement, options);

    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  polygonPointsToCoordinates(polygon: number[][]): string {
    return polygon.map(point => point.join(',')).join(' ');
  }

  goToShop(shop: any): void {
    this.router.navigateByUrl(this.routerHelper.translateRoute(`shops/${shop.shopSlug}`) as string);
  }

  clickOnStage(_event: Event): void {
    // this.circlePosition = [_event.offsetX, _event.offsetY];
    // _event.preventDefault();
  }

  private loadOrders(): void {
    combineLatest([this.authSelector.selectUserId, this.marketplaceId])
      .pipe(
        takeUntil(this.destroyed$),
        skipWhile(([userId]) => !userId),
      )
      .subscribe(([userId, marketplaceId]) => {
        merge(interval(30000), timer(0))
          .pipe(
            takeUntil(this.destroyed$),
            switchMap(() => {
              if (userId) {
                return this.ordersService.getActiveOrders$(userId, marketplaceId);
              }
              return of([]);
            }),
            map(orders => orders.filter(order => order.status === 'READY')),
            map(orders => Object.keys(groupBy(orders, 'shop.slug'))),
            tap(shops => (this.shopsWithOrderReady = shops)),
          )
          .subscribe();
      });
  }

  hasReadyOrder(shop: any): boolean {
    return this.shopsWithOrderReady.includes(shop.shopSlug);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopsCustomMapBlockComponent, { 'keyAliases': {} });
